import { Link, useRouterState } from '@tanstack/react-router';
import {
  Activity,
  KeyRound,
  LayoutDashboard,
  Network,
  Palette,
  ServerCog,
  AlertCircle,
  HardDriveDownload,
  LineChart,
} from 'lucide-react';
import { cn } from '@/lib/utils.js';

interface NavItem {
  to: string;
  label: string;
  // Wide enough to accept Lucide icons (which type className as
  // `string | undefined`) — exactOptionalPropertyTypes makes the
  // unioned-with-undefined shape mandatory.
  icon: React.ComponentType<{ className?: string | undefined }>;
}

const CORE_NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/orgs', label: 'Organisations', icon: Network },
  { to: '/api-keys', label: 'API Keys', icon: KeyRound },
];

// Plugin nav is hard-coded here for the v1 scaffold. Phase 4 wires this to the
// plugin registry so plugins drive their own nav entries.
const PLUGIN_NAV: NavItem[] = [
  { to: '/p/applications', label: 'Applications', icon: ServerCog },
  { to: '/p/issues', label: 'Issues', icon: AlertCircle },
  { to: '/p/backup', label: 'Backups', icon: HardDriveDownload },
  { to: '/p/monitoring', label: 'Monitoring', icon: LineChart },
];

export function Sidebar(): JSX.Element {
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center gap-2 border-b border-border px-5">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
          <Activity className="h-4 w-4" strokeWidth={2.5} />
        </div>
        <span className="text-sm font-semibold tracking-tight">DeployIt</span>
      </div>

      <nav className="flex-1 space-y-6 px-3 py-5">
        <NavSection items={CORE_NAV} />
        <div>
          <div className="px-2 pb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Plugins
          </div>
          <NavSection items={PLUGIN_NAV} />
        </div>
      </nav>

      <div className="border-t border-border p-3">
        <NavLink to="/style-guide" label="Style guide" icon={Palette} />
      </div>
    </div>
  );
}

function NavSection({ items }: { items: NavItem[] }): JSX.Element {
  return (
    <ul className="space-y-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink {...item} />
        </li>
      ))}
    </ul>
  );
}

function NavLink({ to, label, icon: Icon }: NavItem): JSX.Element {
  const { location } = useRouterState();
  const active = to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);
  return (
    <Link
      to={to}
      className={cn(
        'group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors',
        active
          ? 'bg-accent text-accent-foreground'
          : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{label}</span>
    </Link>
  );
}
